import { createSelector, createStructuredSelector } from 'reselect'
import { get, isFunction } from 'lodash'
import { getCurrentPath } from 'src/helpers/browser'
import { selectCurrentHomeUser } from 'src/store/selectors/home'
import { selectBuyerFirstName } from 'src/store/selectors/buyerInfo'
import {
  selectClientHomes,
  selectHasRequestedClient,
  selectHasRequestedHomes,
  selectClientId,
  selectClientIsBuyer
} from 'src/store/selectors/client'
import { selectCustomerBuyersEnabled } from 'src/store/selectors/customerProfile'
import { PATH_HELPERS } from 'src/constants/navigation'

// TODO: do we even need this file anymore? Client state can be single source of truth now.

const STATE_MAP = {
  loading: {
    // used in examples, update if change
    buy: 'buyerInfo.isLoading',
    markets: 'buyerInfo.isLoading',
    home: 'home.loading'
  },
  user: {
    buy: 'buyerInfo',
    markets: 'buyerInfo',
    home: 'client.data'
  },
  userId: {
    buy: 'buyerInfo.id',
    markets: 'buyerInfo.id',
    home: 'client.data.id'
  },
  userLegacyId: {
    buy: 'buyerInfo.legacyRef',
    markets: 'buyerInfo.legacyRef',
    home: 'client.data.legacyRef'
  }
}

const ROUTES = {
  buy: PATH_HELPERS.buy.regex,
  markets: PATH_HELPERS.markets.regex,
  home: PATH_HELPERS.home.regex
}

/**
 * getRouteKeyFromPath
 * @param  {String} pathname [location.pathname]
 * @return {String} [key from Routes]
 */
const getRouteKeyFromPath = pathname => {
  const key = Object.keys(ROUTES).find(k => ROUTES[k].test(pathname))
  return key === 'markets' ? 'buy' : key
}

/**
 * getState
 * @param  {String} stateMapName [key in STATE_MAP to map paths based on route]
 * @param  {Object} state [redux state object]
 * @param  {String} pathname [location.pathname]
 * @return {*} [value of path in state]
 *
 * Ex: [Each pair of examples is equivalent]
 * ex 1. getState('loading', state, '/reports/f1571607-2709-44af-86ae-c379db4b5096/home/5b478a6411f77c0019a467b4')
 *       get(state, 'home.loading')
 * ex 2. getState('loading', state, '/reports/f1571607-2709-44af-86ae-c379db4b5096/buy')
 *       get(state, 'buyerInfo.isLoading')
 */
const getState = (stateMapName, state, pathname) => {
  const routeKey = getRouteKeyFromPath(pathname)
  const path = get(STATE_MAP, [stateMapName, routeKey])

  if (isFunction(path)) {
    return path(state)
  }

  return get(state, path)
}

/**
 * getStateSelectorForKey
 * @param  {String} stateMapName [key in STATE_MAP to map paths based on route]
 * @return {Function} [getState function with bound stateMapName]
 */
const getStateSelectorForKey = stateMapName => (state, pathname) =>
  getState(stateMapName, state, pathname)

/**
 * createFooterSelector
 * [selector factory for footer state, based on current route]
 * @param  {String} stateMapName [key in STATE_MAP to map paths based on route]
 * @return {Function} [Selector for retrieving state]
 */
const createLayoutSelector = stateMapName => {
  const selector = getStateSelectorForKey(stateMapName)
  return state => selector(state, getCurrentPath())
}

/**
 * Begin exported consumption
 */

export const selectRouteKey = () => getRouteKeyFromPath(getCurrentPath())

export const selectLoadingState = createLayoutSelector('loading')

export const selectUserData = createLayoutSelector('user')

export const selectUserFirstName = createSelector(
  selectBuyerFirstName,
  selectCurrentHomeUser,
  (buyerFirstName, homeUser = {}) => buyerFirstName || homeUser.firstName
)

export const selectUserId = createLayoutSelector('userId')
export const selectUserLegacyId = createLayoutSelector('userLegacyId')

export const selectUserIdPair = createStructuredSelector({
  id: selectUserId,
  legacyId: selectUserLegacyId
})

export const selectShowHomesLink = createSelector(
  selectHasRequestedHomes,
  selectClientHomes,
  (requested, homes) => Boolean(requested && homes && homes.length)
)

export const selectShowBuyersLink = createSelector(
  selectRouteKey,
  selectCustomerBuyersEnabled,
  selectHasRequestedClient,
  selectShowHomesLink,
  (routeKey, access, requested, showHomes) =>
    Boolean(
      (routeKey === 'home' && access && requested) ||
        (routeKey === 'buy' && showHomes)
    )
)

const INITIALIZE_ROUTE = PATH_HELPERS.buy.buildPath('initialize')

export const selectMarketExploreRoute = createSelector(
  selectClientId,
  selectClientIsBuyer,
  (clientId, isBuyer) =>
    isBuyer && clientId
      ? PATH_HELPERS.markets.buildPath(clientId, '/explore/map')
      : INITIALIZE_ROUTE
)

export const selectBuyingReportRoute = createSelector(
  selectClientId,
  selectClientIsBuyer,
  (clientId, isBuyer) =>
    isBuyer && clientId
      ? PATH_HELPERS.buy.buildPath(clientId)
      : INITIALIZE_ROUTE
)
